if (!customElements.get('litmus-sticky-purchase')) {
  customElements.define('litmus-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.litmus-sticky-purchase__bar');
      this.trigger = this.querySelector('.litmus-sticky-purchase__trigger');
      this.summary = this.querySelector('.litmus-sticky-purchase__summary');
      this.picker = this.querySelector('.litmus-sticky-purchase__picker');
      this.pickerTitle = this.picker.querySelector('[data-picker-title]');
      this.options = this.picker.querySelector('.litmus-sticky-purchase__options');
      this.optionTemplate = this.querySelector('template[data-picker-option]');
      this.add = this.querySelector('.litmus-sticky-purchase__add');
      this.note = this.querySelector('.litmus-sticky-purchase__note');
      this.error = this.querySelector('.litmus-sticky-purchase__error');
      try {
        this.sellingPlans = JSON.parse(this.querySelector('[data-litmus-selling-plans]')?.textContent || '[]');
      } catch {
        this.sellingPlans = [];
      }
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      };
      this.add.addEventListener('click', () => this.submit(), { signal });
      // The plan label opens the in-place picker; it never scrolls the page to the cards.
      this.trigger.addEventListener('click', () => {
        if (this.picker.open) this.closeOptions(true);
        else this.openOptions();
      }, { signal });
      this.trigger.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.openOptions();
      }, { signal });
      this.options.addEventListener('click', (event) => {
        const option = event.target.closest('.litmus-sticky-purchase__option');
        if (option && !option.disabled) this.selectOption(option);
      }, { signal });
      this.options.addEventListener('keydown', (event) => this.moveOptionFocus(event), { signal });
      // The dialog element itself only receives presses on its backdrop (outside the sheet).
      this.picker.addEventListener('pointerdown', (event) => {
        this.backdropPress = event.target === this.picker;
      }, { signal });
      this.picker.addEventListener('click', (event) => {
        if (event.target.closest('[data-picker-close]') || (event.target === this.picker && this.backdropPress)) {
          this.closeOptions(true);
        }
        this.backdropPress = false;
      }, { signal });
      this.picker.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeOptions(true);
      }, { signal });
      this.picker.addEventListener('close', () => this.trigger.setAttribute('aria-expanded', 'false'), { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.picker.open) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeOptions(true);
      }, { signal });
      document.addEventListener('focusin', (event) => {
        if (!this.picker.open || this.picker.contains(event.target) || event.target === this.trigger) return;
        this.closeOptions();
      }, { signal });
      document.addEventListener('change', this.schedule, { signal });
      document.addEventListener('input', this.schedule, { signal });
      document.addEventListener('product-info:loaded', this.schedule, { signal });
      document.addEventListener('shopify:section:load', this.schedule, { signal });
      document.addEventListener('shopify:section:unload', this.schedule, { signal });
      window.addEventListener('scroll', this.schedule, { signal, passive: true });
      window.addEventListener('resize', this.schedule, { signal, passive: true });
      this.sourceObserver = new MutationObserver(this.schedule);
      this.overlayObserver = new MutationObserver((records) => {
        if (!records.some(({ target }) => !this.contains(target))) return;
        this.syncVisibility();
      });
      this.overlayObserver.observe(document.body, {
        subtree: true, attributes: true, attributeFilter: ['open', 'class', 'aria-hidden'],
      });
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this.bar);
      this.sync();
    }

    disconnectedCallback() {
      this.controller.abort();
      this.sourceObserver.disconnect();
      this.overlayObserver.disconnect();
      this.resizeObserver.disconnect();
      if (this.picker.open) this.picker.close();
      document.documentElement.style.removeProperty('--litmus-purchase-height');
      cancelAnimationFrame(this.frame);
    }

    bindSource() {
      const source = document.querySelector('main product-info[id^="MainProduct-"]');
      const review = source?.querySelector('[data-c02-review-only]') || null;
      if (source === this.source && review === this.review) return;
      this.sourceObserver.disconnect();
      this.closeOptions();
      this.source = source;
      this.review = review;
      this.pickerKey = null;
      this.options.replaceChildren();
      if (!source) return;
      this.sourceObserver.observe(source, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'checked', 'value', 'hidden', 'class'],
      });
    }

    sync() {
      this.bindSource();
      this.mainAdd = this.review?.querySelector('.litmus-review-cta') || this.source?.querySelector('product-form [name="add"]');
      this.hidden = !this.mainAdd;
      if (this.hidden) {
        this.closeOptions();
        return;
      }
      this.productForm = this.source.querySelector('product-form');
      this.form = this.productForm?.querySelector('form');
      /*
        review: C02 design fixture. cards: Litmus delivery cards own the plan choice.
        simple: anything else (Recharge widget, plain product) — one add-to-cart button.
      */
      this.cards = this.review ? null : this.source.querySelector('litmus-purchase-options');
      const mode = this.review ? 'review' : this.cards ? 'cards' : 'simple';
      if (this.dataset.mode !== mode) this.dataset.mode = mode;
      if (this.review) this.syncReview();
      else this.syncProduct();
      if (this.picker.open) this.syncPicker();
      this.measureBar();
      this.syncVisibility();
    }

    measureBar() {
      this.removeAttribute('data-compact');
      const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
      const available = Math.max(0, window.innerHeight - Math.max(0, headerBottom) - 16);
      const compact = this.bar.getBoundingClientRect().height > available;
      this.style.setProperty('--litmus-purchase-available', `${available}px`);
      this.toggleAttribute('data-compact', compact);
      // On <html>, so the root scroll-padding-bottom in component-litmus-sticky-purchase.css can read it.
      document.documentElement.style.setProperty('--litmus-purchase-height', `${this.bar.getBoundingClientRect().height}px`);
      if (this.picker.open) this.placePicker();
    }

    write(element, value) {
      if (element.textContent !== value) element.textContent = value;
    }

    // Fields a target lacks are skipped; [data-value] inside a field receives its text.
    renderOffer(target, fields) {
      for (const name of ['offer', 'badge', 'price', 'compare', 'unit', 'shipment', 'saving', 'cadence', 'compact-charge',
        'total-price', 'total-compare', 'count']) {
        const element = target.querySelector(`[data-${name}]`);
        if (!element) continue;
        const value = fields[name] || '';
        this.write(element.querySelector('[data-value]') || element, value);
        element.hidden = !value;
      }
    }

    priceView({ total, unit, compare, unitCompare, count, currency, perPack = false, packs = perPack, unitLabel = '' }) {
      const values = [total, unit, compare, unitCompare, count];
      if (values.some((value) => value == null || String(value).trim() === '')) return null;
      [total, unit, compare, unitCompare, count] = values.map(Number);
      if (![total, unit, compare, unitCompare].every((value) => Number.isSafeInteger(value) && value >= 0) ||
        !Number.isSafeInteger(count) || count < 1 || !/^[A-Z]{3}$/.test(currency)) return null;
      const perUnit = this.dataset.priceDisplay === 'per_unit';
      const money = (cents) => new Intl.NumberFormat(perPack ? 'en-US' : document.documentElement.lang || 'en', {
        style: 'currency', currency,
      }).format(cents / 100);
      const price = perUnit ? unit : total;
      const comparison = perUnit ? unitCompare : compare;
      const charge = perPack ? `Shipment total ${money(total)} · ${count} ${count === 1 ? 'jar' : 'jars'}` :
        `${this.dataset.totalLabel} ${money(total)} · ${this.dataset.quantity}: ${count}`;
      return {
        price: money(price),
        compare: comparison > price ? money(comparison) : '',
        unit: perUnit ? (perPack ? `per jar${unit * count !== total ? ' · approx.' : ''}` : unitLabel || this.dataset.unitLabel) : '',
        shipment: perUnit ? charge : '',
        'compact-charge': charge,
        // Phone row: always the shipment total, the amount the add-to-cart charges.
        'total-price': money(total),
        'total-compare': compare > total ? money(compare) : '',
        count: count > 1 ? (packs ? `${count} jars` : `${this.dataset.quantity}: ${count}`) : '',
      };
    }

    badgeText(card) {
      return card.querySelector('.litmus-offer__badge, .litmus-review-offer__banner')?.textContent.trim() || '';
    }

    reviewFields(label) {
      const data = label.dataset;
      const pricing = data.priceValid === 'true' ? this.priceView({
        total: data.totalCents, unit: data.unitCents,
        compare: data.compareCents, unitCompare: data.unitCompareCents,
        count: data.packs, currency: data.currency, perPack: true,
      }) : null;
      return {
        offer: label.querySelector('strong')?.textContent.trim() || '',
        badge: this.badgeText(label),
        cadence: label.querySelector('.litmus-review-offer__cadence')?.textContent.trim() || '',
        ...(pricing || { price: 'Price unavailable', 'compact-charge': 'Price unavailable', 'total-price': 'Price unavailable' }),
        saving: pricing ? label.querySelector('.litmus-review-offer__saving')?.textContent.trim() || '' : '',
      };
    }

    syncReview() {
      const checked = this.review.querySelector('.litmus-review-offer input[type="radio"]:checked')?.closest('.litmus-review-offer');
      if (checked) this.renderOffer(this.summary, this.reviewFields(checked));
      const sourceNotice = this.review.querySelector('.litmus-review-note')?.textContent.trim() || '';
      const previewContext = sourceNotice.includes(' · ') ? sourceNotice.split(' · ')[0] : 'Design preview';
      this.write(this.note, `${previewContext} · Purchasing disabled.`);
      this.note.hidden = !this.note.textContent;
      this.error.hidden = true;
      this.add.disabled = true;
      this.add.removeAttribute('aria-busy');
      this.write(this.add, this.dataset.add);
    }

    // Last non-disabled selling_plan value the main form would post (the last value wins).
    formPlan() {
      if (!this.form) return '';
      const values = new FormData(this.form).getAll('selling_plan');
      return String(values[values.length - 1] || '');
    }

    // Selected variant id and its one-time price, as the bar prices it.
    productContext() {
      const id = this.form?.elements.namedItem('id')?.value;
      let variant;
      try {
        variant = JSON.parse(this.source.querySelector('[data-selected-variant]')?.textContent || 'null');
      } catch {
        variant = null;
      }
      const current = String(variant?.id) === id;
      const original = this.source.dataset.productId === this.dataset.productId && id === this.dataset.variantId;
      return {
        id,
        original,
        price: current ? variant.price : original ? Number(this.dataset.unitPrice) : NaN,
        compare: current ? variant.compare_at_price : original ? this.dataset.comparePrice : 0,
      };
    }

    // Bar price fields for a selling plan ('' for one-time) and pack count.
    // A plan the theme cannot price (for example an app-only plan) has no price.
    planPricing({ plan, quantity, context, card = null }) {
      const selectedPlan = this.sellingPlans.find(({ id: planId }) => String(planId) === plan);
      const price = selectedPlan ? Number(selectedPlan.price) : plan ? NaN : context.price;
      const priced = Number.isSafeInteger(price) && price >= 0 && Number.isSafeInteger(price * quantity) &&
        /^[A-Z]{3}$/.test(this.dataset.currency);
      const compare = Number(selectedPlan?.compare ?? context.compare) || 0;
      const pricing = priced ? this.priceView({
        total: price * quantity, unit: price, compare: compare * quantity, unitCompare: compare,
        count: quantity, currency: this.dataset.currency, packs: !!this.cards,
        unitLabel: card?.querySelector('.litmus-review-offer__unit-label')?.textContent.trim() || '',
      }) : null;
      return { selectedPlan, priced, pricing };
    }

    // One picker row per Litmus delivery card, priced as the bar would show it once chosen.
    cardFields(card, context) {
      const packs = Number(card.dataset.packs);
      const quantity = Number.isInteger(packs) && packs > 0 ? packs : 1;
      const { pricing } = this.planPricing({ plan: card.dataset.planId || '', quantity, context, card });
      const fallback = card.dataset.totalMoney || '';
      return {
        offer: card.querySelector('.litmus-offer__title')?.textContent.trim() || '',
        badge: this.badgeText(card),
        cadence: card.querySelector('.litmus-review-offer__cadence')?.textContent.trim() || '',
        ...(pricing || { price: fallback, 'total-price': fallback }),
        saving: pricing ? card.querySelector('.litmus-review-offer__saving')?.textContent.trim() || '' : '',
      };
    }

    syncProduct() {
      const simple = this.dataset.mode === 'simple';
      const context = this.productContext();
      const id = context.id;
      const quantityInput = this.form?.elements.namedItem('quantity');
      const plan = simple ? this.formPlan() : String(this.form?.elements.namedItem('selling_plan')?.value || '');
      const selectedPlan = this.sellingPlans.find(({ id: planId }) => String(planId) === plan);
      if (plan !== this.lastPlan && quantityInput) {
        const nextQuantity = selectedPlan?.quantity || 1;
        if (Number(quantityInput.value) !== nextQuantity) {
          quantityInput.value = String(nextQuantity);
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.lastPlan = plan;
      }
      const quantity = quantityInput ? Number(quantityInput.value) : 1;
      const card = this.cards?.querySelector('input[type="radio"]:checked')?.closest('.litmus-review-offer');
      // Simple mode: a plan the theme cannot price (for example an app-only plan) still submits, without a price.
      const { priced, pricing: planView } = this.planPricing({ plan, quantity, context, card });
      const pending = !!this.source.pendingRequestUrl;
      const quantityInvalid = !Number.isInteger(quantity) || quantity < 1 || (quantityInput && !quantityInput.checkValidity());
      this.incomplete = simple ?
        !id || quantityInvalid || (this.dataset.requiresPlan === 'true' && !plan) || pending :
        !id || !priced || quantityInvalid || (plan && !selectedPlan) ||
          (context.original && this.dataset.requiresPlan === 'true') || pending;
      const busy = this.mainAdd.classList.contains('loading');
      const disabled = this.mainAdd.disabled || this.mainAdd.getAttribute('aria-disabled') === 'true';
      this.add.disabled = busy || (disabled && !this.incomplete);
      this.add.setAttribute('aria-busy', String(busy));
      const pricing = this.incomplete ? null : planView;
      const ready = simple ? [this.dataset.addLabel, pricing?.['total-price']].filter(Boolean).join(' · ') : this.dataset.add;
      this.write(this.add, busy ? this.dataset.loading : this.incomplete ? this.dataset.choose : disabled ? this.mainAdd.innerText.trim() : ready);
      const cardTitle = card?.querySelector('.litmus-offer__title')?.textContent.trim();
      this.renderOffer(this.summary, {
        offer: this.incomplete ? this.dataset.choose : cardTitle || selectedPlan?.name || this.source.querySelector('.product__title h1')?.textContent.trim() || '',
        ...pricing,
        saving: pricing ? card?.querySelector('.litmus-review-offer__saving')?.textContent.trim() || '' : '',
        cadence: !this.incomplete && selectedPlan ? `${quantity} ${quantity === 1 ? 'jar' : 'jars'} per shipment` :
          !this.incomplete && this.dataset.priceDisplay !== 'per_unit' ? `${this.dataset.quantity}: ${quantity}` : '',
      });
      this.note.hidden = true;
      const errorWrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
      const error = errorWrapper && !errorWrapper.hidden ? errorWrapper.textContent.trim() : '';
      this.write(this.error, error);
      this.error.hidden = !error;
    }

    // Radios of the delivery cards the picker mirrors: the Litmus cards or the review fixture.
    pickerInputs() {
      const root = this.review || this.cards;
      return root ? [...root.querySelectorAll('.litmus-review-offer input[type="radio"][id]')] : [];
    }

    syncPicker() {
      const inputs = this.pickerInputs();
      const key = inputs.map(({ id }) => id).join(' ');
      if (key !== this.pickerKey) {
        this.pickerKey = key;
        this.options.replaceChildren(...inputs.map((input) => {
          const option = this.optionTemplate.content.firstElementChild.cloneNode(true);
          option.dataset.sourceId = input.id;
          return option;
        }));
      }
      const legend = (this.review || this.cards)?.querySelector('legend')?.textContent.trim();
      if (legend) this.write(this.pickerTitle, legend);
      const context = this.review ? null : this.productContext();
      const options = [...this.options.children];
      for (const option of options) {
        const input = document.getElementById(option.dataset.sourceId);
        const card = input?.closest('.litmus-review-offer');
        const fields = !card ? {} : this.review ? this.reviewFields(card) : this.cardFields(card, context);
        // Rows name the shipment only when it holds more than one pack.
        if (!fields.count) fields.shipment = '';
        this.renderOffer(option, fields);
        option.setAttribute('aria-checked', String(!!input?.checked));
        option.disabled = !card || input.disabled;
      }
      const enabled = options.filter((option) => !option.disabled);
      if (!enabled.some((option) => option.tabIndex === 0)) {
        this.roveTo(enabled.find((option) => option.getAttribute('aria-checked') === 'true') || enabled[0]);
      }
      return enabled;
    }

    roveTo(option) {
      for (const item of this.options.children) item.tabIndex = item === option ? 0 : -1;
    }

    // Keeps a row visible inside the list without scrolling the page.
    revealOption(option) {
      const list = this.options.getBoundingClientRect();
      const item = option.getBoundingClientRect();
      if (item.top < list.top) this.options.scrollTop -= list.top - item.top;
      else if (item.bottom > list.bottom) this.options.scrollTop += item.bottom - list.bottom;
    }

    // Arrow keys move focus between rows; Enter, Space or a tap chooses the focused row.
    moveOptionFocus(event) {
      const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
      if (!step && event.key !== 'Home' && event.key !== 'End') return;
      const options = [...this.options.querySelectorAll('.litmus-sticky-purchase__option:not(:disabled)')];
      if (!options.length) return;
      event.preventDefault();
      const index = options.indexOf(event.target.closest('.litmus-sticky-purchase__option'));
      const next = event.key === 'Home' ? options[0] : event.key === 'End' ? options[options.length - 1] :
        options[(Math.max(index, 0) + step + options.length) % options.length];
      this.roveTo(next);
      next.focus({ preventScroll: true });
      this.revealOption(next);
    }

    // Larger screens: a popover above the bar, aligned with the trigger. Phones use the CSS bottom sheet.
    placePicker() {
      const trigger = this.trigger.getBoundingClientRect();
      const barTop = this.bar.getBoundingClientRect().top;
      const viewport = document.documentElement.clientWidth;
      const headerBottom = Math.max(0, document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
      const width = Math.min(Math.max(trigger.width, 360), viewport - 32);
      const left = Math.min(Math.max(16, trigger.left), viewport - 16 - width);
      this.picker.style.setProperty('--picker-left', `${left}px`);
      this.picker.style.setProperty('--picker-width', `${width}px`);
      this.picker.style.setProperty('--picker-bottom', `${Math.max(0, window.innerHeight - barTop) + 12}px`);
      this.picker.style.setProperty('--picker-max-height', `${Math.max(160, barTop - headerBottom - 24)}px`);
    }

    openOptions() {
      this.sync();
      if (this.picker.open || this.hidden || this.bar.inert) return;
      const options = this.syncPicker();
      const current = options.find((option) => option.getAttribute('aria-checked') === 'true') || options[0];
      if (!current) {
        this.chooseOptions();
        return;
      }
      this.roveTo(current);
      // The dialog hands focus back to what had it before opening: keep that the trigger, in the fixed bar.
      this.trigger.focus({ preventScroll: true });
      this.placePicker();
      this.picker.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      current.focus({ preventScroll: true });
      this.revealOption(current);
    }

    closeOptions(restoreFocus = false) {
      if (this.picker.open) this.picker.close();
      if (this.trigger.getAttribute('aria-expanded') !== 'false') this.trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus && !this.bar.inert) this.trigger.focus({ preventScroll: true });
    }

    /*
      Checks the matching card in the buy box and lets its own change event drive everything
      that follows (card state, hidden selling_plan/quantity inputs, add-to-cart label price,
      this bar). The page is not scrolled.
    */
    selectOption(option) {
      const input = document.getElementById(option.dataset.sourceId);
      if (!input || input.disabled) return;
      this.closeOptions(true);
      if (input.checked) return;
      const anchor = this.viewAnchor();
      input.checked = true;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
      this.holdAnchor(anchor);
    }

    /*
      The selected card expands its details, so the buy box (above the screen while this bar
      shows) can change height. Keep what is on screen still: browsers with scroll anchoring
      already compensate and need no shift; others are shifted back by the same amount.
    */
    viewAnchor() {
      const element = document.elementFromPoint(document.documentElement.clientWidth / 2, window.innerHeight / 2);
      return element && !this.contains(element) ? { element, top: element.getBoundingClientRect().top } : null;
    }

    holdAnchor(anchor) {
      if (!anchor?.element.isConnected) return;
      const shift = anchor.element.getBoundingClientRect().top - anchor.top;
      if (Math.abs(shift) >= 1) window.scrollBy({ top: shift, behavior: 'instant' });
    }

    syncVisibility() {
      const overlay = document.querySelector(
        'cart-drawer.active, #cart-notification.active, modal-dialog[open], product-modal[open], ' +
        'quick-add-modal[open], pickup-availability-drawer[open], dialog[open]:not(.litmus-sticky-purchase__picker), ' +
        'header-drawer details[open], menu-drawer details[open], details-modal details[open]'
      );
      // Shown only once the main add-to-cart has scrolled above the viewport, and never over the closing CTA banner.
      const banner = [...document.querySelectorAll('.litmus-cta-banner')].some((element) => {
        const rect = element.getBoundingClientRect();
        return rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0;
      });
      const visible = !this.hidden && this.mainAdd?.isConnected && this.mainAdd.getBoundingClientRect().bottom <= 0 && !overlay && !banner;
      const appearing = !!visible && !this.hasAttribute('data-visible');
      this.toggleAttribute('data-visible', !!visible);
      // The bar usually appears because keyboard focus just scrolled the page, so the element
      // that took focus can be sitting where the bar now is. Lift it clear (WCAG 2.4.11).
      if (appearing) {
        const focused = document.activeElement;
        const barTop = window.innerHeight - this.bar.getBoundingClientRect().height;
        const rect = focused?.getBoundingClientRect?.();
        if (rect && focused !== document.body && !this.contains(focused) && focused.matches(':focus-visible') &&
          rect.bottom > barTop && rect.top < window.innerHeight) {
          requestAnimationFrame(() => focused.scrollIntoView({ block: 'nearest' }));
        }
      }
      this.bar.inert = !visible;
      if (!visible) this.closeOptions();
      if (overlay && this.restoreAfterCart) this.waitingForCartClose = true;
      if (!overlay && this.waitingForCartClose) {
        if (document.activeElement === document.body || document.activeElement === this.add ||
          document.activeElement?.closest?.('cart-notification, cart-drawer')) {
          (visible ? this.add : this.mainAdd).focus({ preventScroll: true });
        }
        this.restoreAfterCart = false;
        this.waitingForCartClose = false;
      }
    }

    // Scrolls to the buy box's purchase controls. Used by other sections' purchase links and when
    // the bar cannot add (for example no priced option); the plan label uses the picker instead.
    chooseOptions() {
      const target = this.source?.querySelector('.litmus-review-offer input:checked, .rc-widget input:not([disabled]), variant-selects input:not([disabled]), variant-selects select, input[name="quantity"]') || this.mainAdd;
      if (!target) return;
      this.closeOptions();
      const header = document.querySelector('.section-header');
      const top = target.getBoundingClientRect().top + window.scrollY - (header?.getBoundingClientRect().height || 0) - 24;
      target.focus({ preventScroll: true });
      window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
      this.syncVisibility();
    }

    submit() {
      this.sync();
      if (this.review || this.add.disabled) return;
      if (this.incomplete || !this.form || !this.productForm?.form) return this.chooseOptions();
      this.restoreAfterCart = true;
      this.form.requestSubmit(this.mainAdd);
      this.sync();
    }
  });
}
